/**
 * @fileoverview System-web local database provider and replication state context.
 * @module system-web/local-db
 *
 * Responsibilities:
 * - Open the system Dexie database and start background replication when auth is ready.
 * - Expose sync state, conflicts, and resync controls to the rest of system-web.
 *
 * Dependencies:
 * - @firesquirrel/local-db
 * - ../auth/AuthProvider
 * - ../contexts/SystemAppModeContext
 *
 * Architecture:
 * - Children render immediately while DB and sync work continue in the background.
 * - System-web pages must read from Dexie-backed services rather than direct API calls.
 *
 * Notes:
 * - Server-authoritative library collections can be synced into Dexie without being client-pushable.
 */

import React, { createContext, useContext, useState, useRef, useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import {
  getSystemDb,
  destroySystemDb,
  getSystemDexieRaw,
  setupReplication,
  cancelAllReplication,
  validateSyncNonce,
  wakeLocalWrite,
  SYSTEM_COLLECTION_MAP,
  SYSTEM_PUSH_ALLOWED_COLLECTIONS,
  EXPECTED_SYSTEM_DB_SCHEMA_VERSION,
  MIN_MIGRATABLE_SYSTEM_VERSION,
  SYSTEM_MIGRATION_REGISTRY,
  clearAllSyncCheckpoints,
  useLocalDatabase,
  triggerServiceWorkerUpdateAndReload,
  LocalDatabaseErrorScreen,
  wireReplicationStates,
  setSseStatus as setSharedSseStatus,
  triggerFullSync,
} from '@firesquirrel/local-db';
import type {
  SystemDatabase,
  NamedReplicationStates,
  SyncConflict,
  ReplicationConfig,
  DbStatus,
  DbId,
  ExpectedDbMeta,
} from '@firesquirrel/local-db';
import { getToken } from '../apiClient';
import { useAuth } from '../auth/AuthProvider';
import { getWebConfig, type LocalDbMetadataConfig } from '../config';


const SYSTEM_SYNC_CONTRACT_VERSION = '2026-05-28-system-sync-startup-reconciliation-v1';

function getSystemSyncContractKey(): string {
  return 'firesquirrel:system:sync-contract-version';
}

async function waitForSystemToken(timeoutMs = 5000): Promise<string | null> {
  const startedAt = Date.now();
  while (Date.now() - startedAt < timeoutMs) {
    const token = await getToken();
    if (token) {
      return token;
    }
    await new Promise((resolve) => setTimeout(resolve, 100));
  }
  return await getToken();
}

/**
 * @contract CollectionSyncStatus
 *
 * Purpose:
 * - Represents the current replication state for one synced system collection.
 *
 * Fields:
 * - idle: Collection is not actively syncing.
 * - active: Collection sync loop is currently running.
 * - error: Collection encountered a replication error.
 * - cancelled: Collection replication was cancelled.
 *
 * Rules:
 * - Values must remain aligned with replication state transitions.
 *
 * Sync:
 * - Derived from client-side replication observables.
 */
export type CollectionSyncStatus = 'idle' | 'active' | 'error' | 'cancelled';

/**
 * @contract CollectionSyncInfo
 *
 * Purpose:
 * - Carries sync status and recent error/success metadata for one system collection.
 *
 * Fields:
 * - name: Collection name.
 * - status: Current sync status.
 * - lastError: Optional latest replication error string.
 * - lastSyncAt: Optional timestamp of the latest idle transition.
 *
 * Rules:
 * - Consumers should treat timestamps as informational only.
 *
 * Sync:
 * - Updated from client-side replication state subscriptions.
 */
export interface CollectionSyncInfo {
  name: string;
  status: CollectionSyncStatus;
  lastError?: string;
  lastSyncAt?: string;
}

/**
 * @contract SseConnectionStatus
 *
 * Purpose:
 * - Describes the current server push connectivity mode for system replication.
 *
 * Fields:
 * - live: Primary push connection is healthy.
 * - polling: Fallback polling mode is active.
 * - disconnected: No active push connectivity is established.
 *
 * Rules:
 * - Values are display/status metadata and do not replace per-collection replication state.
 *
 * Sync:
 * - Derived from shared replication transport status.
 */
export type SseConnectionStatus = 'live' | 'polling' | 'disconnected';

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------
interface SystemDbContextValue {
  ready: boolean;
  db: SystemDatabase | null;
  replicationStates: NamedReplicationStates | null;
  syncing: boolean;
  sseStatus: SseConnectionStatus;
  collectionStatuses: Record<string, CollectionSyncInfo>;
  conflicts: SyncConflict[];
  resolveConflict: (conflictId: string, resolution: 'keep-local' | 'accept-server') => Promise<void>;
  dismissConflict: (conflictId: string) => void;
  clearResolvedConflicts: () => void;
  forceResync: () => void;
  /** Local database lifecycle status from the localDatabaseManager. */
  dbStatus: DbStatus;
  /** Whether writes are currently allowed (true only during 'ready' and 'syncing-pending-writes'). */
  canWrite: boolean;
}

/**
 * @localFirst
 *
 * Source of Truth:
 * - local
 *
 * Write Flow:
 * - Local system collections write to Dexie first, then replication pushes allowed collections in the background.
 *
 * Sync Strategy:
 * - Auth-gated replication plus nonce validation keeps system Dexie aligned with MongoDB.
 *
 * Conflict Resolution:
 * - Sync conflicts are surfaced through provider state for caller-managed resolution.
 */

const SystemDbContext = createContext<SystemDbContextValue>({
  ready: false,
  db: null,
  replicationStates: null,
  syncing: false,
  sseStatus: 'disconnected',
  collectionStatuses: {},
  conflicts: [],
  resolveConflict: async () => {},
  dismissConflict: () => {},
  clearResolvedConflicts: () => {},
  forceResync: () => {},
  dbStatus: 'initializing',
  canWrite: false,
});

/**
 * @hook useSystemDb
 *
 * Purpose:
 * - Exposes system Dexie readiness, sync state, and conflict controls from context.
 *
 * Returns:
 * - The current `SystemDbContextValue` for the active system-web session.
 *
 * Side Effects:
 * - None; this hook reads provider state only.
 *
 * Offline Behavior:
 * - Continues exposing locally cached DB state even when replication is disconnected.
 */
export function useSystemDb(): SystemDbContextValue {
  return useContext(SystemDbContext);
}

export type { SyncConflict };

/**
 * @component SystemDBProvider
 *
 * Purpose:
 * - Provides the system Dexie database handle and replication status to descendant components.
 *
 * Props:
 * - children: Routed application content that should have access to system DB state.
 *
 * Behavior:
 * - Opens Dexie, validates sync identity, starts background replication, tracks conflicts, and exposes sync state through context.
 *
 * Data Source:
 * - local
 *
 * Constraints:
 * - Must not block child rendering while database or replication startup occurs.
 *
 * Performance:
 * - Reuses refs for live DB and replication state to avoid unnecessary reinitialization.
 */
export function SystemDBProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [syncing, setSyncing] = useState(false);
  const [sseStatus, setSseStatus] = useState<SseConnectionStatus>('disconnected');
  const [collectionStatuses, setCollectionStatuses] = useState<Record<string, CollectionSyncInfo>>({});
  const [conflicts, setConflicts] = useState<SyncConflict[]>([]);

  // dbRef lets callbacks close over the live DB reference without forward-declaring it
  const dbRef = useRef<SystemDatabase | null>(null);
  const replicationRef = useRef<NamedReplicationStates | null>(null);
  const syncConfigRef = useRef<ReplicationConfig | null>(null);

  // Wire per-collection status listeners — defined first, used by startSync
  const wireReplicationListeners = useCallback((states: NamedReplicationStates) => {
    for (const [name, state] of states.entries()) {
      setCollectionStatuses((prev) => ({ ...prev, [name]: { name, status: 'idle' } }));

      state.active$.subscribe((active: boolean) => {
        if (active) setSyncing(true);
        setCollectionStatuses((prev) => ({
          ...prev,
          [name]: {
            ...prev[name],
            name,
            status: active ? 'active' : 'idle',
            ...(active ? {} : { lastSyncAt: new Date().toISOString() }),
          },
        }));
        if (!active) {
          setTimeout(() => {
            setCollectionStatuses((current) => {
              const allIdle = Object.values(current).every((c) => c.status !== 'active');
              if (allIdle) setSyncing(false);
              return current;
            });
          }, 100);
        }
      });

      state.error$.subscribe((err: any) => {
        setCollectionStatuses((prev) => ({
          ...prev,
          [name]: { ...prev[name], name, status: 'error', lastError: err?.message ?? String(err) },
        }));
      });

      state.canceled$.subscribe(() => {
        setCollectionStatuses((prev) => ({
          ...prev,
          [name]: { ...prev[name], name, status: 'cancelled' },
        }));
      });
    }
  }, []);

  // Conflict handler — deduplicates by collection+documentId
  const handleConflict = useCallback((conflict: SyncConflict) => {
    if (conflict.resolved || conflict.autoMerged) {
      console.log('[Sync] Conflict auto-merged:', conflict.collection, conflict.documentId);
    } else {
      console.warn('[Sync] Conflict detected:', conflict.collection, conflict.documentId);
    }
    setConflicts((prev) => {
      const idx = prev.findIndex(
        (c) => !c.resolved && c.collection === conflict.collection && c.documentId === conflict.documentId,
      );
      if (idx >= 0) {
        const updated = [...prev];
        updated[idx] = conflict;
        return updated;
      }
      return [...prev, conflict];
    });
  }, []);

  // Manager config — stable reference for the hook. All functions are
  // module-level singletons; the constants are module-level imports.
  const managerConfig = useMemo(() => ({
    dbId: 'system-web' as DbId,
    dbName: 'firesquirrel-system',
    openDb: getSystemDb as () => Promise<SystemDatabase>,
    closeDb: destroySystemDb,
    getRawDexie: getSystemDexieRaw,
    expectedSchemaVersion: EXPECTED_SYSTEM_DB_SCHEMA_VERSION,
    minMigratableVersion: MIN_MIGRATABLE_SYSTEM_VERSION,
    migrationRegistry: SYSTEM_MIGRATION_REGISTRY,
    collectionMap: SYSTEM_COLLECTION_MAP,
    checkpointPrefix: 'system',
    onUpdateAppFirst: triggerServiceWorkerUpdateAndReload,
  }), []);

  // Map web-config's localDbMetadata to ExpectedDbMeta for the manager.
  // When web-config hasn't loaded yet (or doesn't include localDbMetadata),
  // this is null — the hook's 10-second fallback timeout will then proceed
  // with compiled-in constants (the manager aligns environment/apiBaseUrl/
  // appVersion with local _db_meta to avoid false wipes).
  const expectedMetadata = useMemo<ExpectedDbMeta | null>(() => {
    const meta = getWebConfig().localDbMetadata as LocalDbMetadataConfig | null | undefined;
    if (!meta) return null;
    return {
      dbId: meta.dbId as DbId,
      environment: meta.environment as ExpectedDbMeta['environment'],
      schemaVersion: meta.schemaVersion,
      appVersion: meta.appVersion,
      apiBaseUrl: meta.apiBaseUrl,
    };
  }, []);

  const startSync = useCallback(
    async (openedDb: SystemDatabase) => {
      // Validate DB identity nonce BEFORE starting replication.
      // If Dexie was wiped (browser clear, schema upgrade), this clears
      // all stale localStorage checkpoints so every collection does a full pull.
      const rawDexie = getSystemDexieRaw();
      if (rawDexie) {
        const contractKey = getSystemSyncContractKey();
        const storedContract = (() => {
          try { return localStorage.getItem(contractKey); } catch { return null; }
        })();
        if (storedContract !== SYSTEM_SYNC_CONTRACT_VERSION) {
          await clearAllSyncCheckpoints(SYSTEM_COLLECTION_MAP, rawDexie.table('_sync_meta'));
          try { localStorage.setItem(contractKey, SYSTEM_SYNC_CONTRACT_VERSION); } catch { /* ignore */ }
          console.log('[sync] System sync contract changed — full pull forced');
        }
        await validateSyncNonce(rawDexie, 'system', SYSTEM_COLLECTION_MAP);
      }
      const startupToken = await waitForSystemToken();
      if (!startupToken) {
        console.error('[sync] Cannot start system replication — access token is not available.');
        return null;
      }
      const config: ReplicationConfig = {
        apiBaseUrl: '/api',
        departmentId: 'system',
        checkpointPrefix: 'system',
        collectionMap: SYSTEM_COLLECTION_MAP,
        pushAllowedCollections: SYSTEM_PUSH_ALLOWED_COLLECTIONS,
        // geo_cache_files is server-authoritative and hard-deleted on the
        // server, so missed WebSocket delete events cannot be recovered by a
        // delta pull. Include it here so the divergence check can reconcile
        // disconnected clients against the authoritative server collection.
        divergenceCheckCollections: ['code_set_raw_artifacts', 'background_jobs', 'geo_cache_files'],
        onConflict: handleConflict,
        useWebSocket: true,
        getAuthHeaders: async (): Promise<Record<string, string>> => {
          const token = await waitForSystemToken();
          return token ? { Authorization: `Bearer ${token}` } : {};
        },
      };
      const states = setupReplication(openedDb as any, config);
      wireReplicationListeners(states);
      wireReplicationStates(states);
      replicationRef.current = states;
      syncConfigRef.current = config;
      console.log('[db] System database opened and replication started');
      return states;
    },
    [handleConflict, wireReplicationListeners],
  );

  // useLocalDatabase — enforces: DB never opens, sync never starts, until
  // isAuthenticated=true. The localDatabaseManager validates _db_meta against
  // expectedMetadata, executes recovery (incremental migration, wipe-and-rebuild,
  // or update-app-first), and reports status for the sync widget.
  // NOTE: collectionKeys is intentionally NOT passed for system-web.
  // System DB has no tenant concept and never changes — clearing pull
  // checkpoints on every refresh would trigger unnecessary full pulls
  // that overwrite un-pushed local changes (e.g. pending deletes).
  // See DOC-5 in useAuthGatedDb.ts. Use forceResync() for manual full reconciliation.
  const { status, db, canWrite, replicationStates, error: dbError } = useLocalDatabase<SystemDatabase>({
    isAuthenticated,
    dbId: 'system-web',
    managerConfig,
    expectedMetadata,
    isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
    startSync,
  });

  // Keep refs in sync with hook output for use in callbacks
  dbRef.current = db;

  const ready = status === 'ready';

  const resolveConflict = useCallback(async (
    conflictId: string,
    resolution: 'keep-local' | 'accept-server',
  ) => {
    setConflicts((prev) => {
      const conflict = prev.find((c) => c.id === conflictId);
      if (conflict && dbRef.current) {
        const currentDb = dbRef.current as any;
        const table = currentDb[conflict.collection]?.table;
        if (table) {
          if (resolution === 'keep-local') {
            const bumped = { ...conflict.localState, updatedAt: new Date().toISOString() };
            table.put(bumped)
              .then(() => wakeLocalWrite(conflict.collection))
              .catch((err: any) =>
                console.warn('[Sync] Failed to bump local doc for re-push:', err),
              );
          } else {
            table.put({ ...conflict.serverState }).catch((err: any) =>
              console.warn('[Sync] Failed to apply server state to Dexie:', err),
            );
          }
        }
      }
      return prev.map((c) =>
        c.id === conflictId ? { ...c, resolved: true, resolution } : c,
      );
    });
    console.log(`[Sync] Conflict ${conflictId} resolved: ${resolution}`);
  }, []);

  const dismissConflict = useCallback((conflictId: string) => {
    setConflicts((prev) => prev.filter((c) => c.id !== conflictId));
  }, []);

  const clearResolvedConflicts = useCallback(() => {
    setConflicts((prev) => prev.filter((c) => !c.resolved));
  }, []);

  // forceResync — clear all sync checkpoints and restart replication
  const forceResync = useCallback(async () => {
    // Cancel active replication loops first so an in-flight pull cannot rewrite
    // checkpoints after clearAllSyncCheckpoints deletes them.
    if (replicationRef.current) {
      await cancelAllReplication(replicationRef.current);
      replicationRef.current = null;
    }
    const rawDexie = getSystemDexieRaw();
    let metaTable: unknown;
    try { metaTable = rawDexie?.table('_sync_meta'); } catch { /* _sync_meta not yet available — non-fatal */ }
    await clearAllSyncCheckpoints(SYSTEM_COLLECTION_MAP, metaTable, 'system');
    await clearAllSyncCheckpoints(SYSTEM_COLLECTION_MAP, metaTable);
    if (dbRef.current && syncConfigRef.current) {
      const states = setupReplication(dbRef.current as any, syncConfigRef.current);
      replicationRef.current = states;
      wireReplicationListeners(states);
      wireReplicationStates(states);
      console.log('[sync] Force resync started — all collections will do full reconciliation');
    }
    // Trigger sync to actually pull data after restarting replication —
    // parity with tenant-web's forceResync. Without this wake, the restarted
    // collection loops sit idle until their next scheduled tick and the
    // Force Full Sync button appears to do nothing.
    triggerFullSync();
  }, [wireReplicationListeners]);

  const ctxValue: SystemDbContextValue = {
    ready,
    db,
    replicationStates,
    syncing,
    sseStatus,
    collectionStatuses,
    conflicts,
    resolveConflict,
    dismissConflict,
    clearResolvedConflicts,
    forceResync,
    dbStatus: status,
    canWrite,
  };

  // Gate children until the lifecycle manager exposes a usable DB. During
  // wipe-and-rebuild it changes status to 'recovering' before closing and
  // deleting IndexedDB; rendering children in that interval lets their
  // liveQuery subscriptions reopen the closing database and block deletion.
  // The DB remains null until validation/recovery completes, including while
  // pending writes are synchronized before a mobile recovery.
  const isOpening = db === null;

  // Re-show boot splash while the DB is unavailable (App.tsx hides it when
  // auth loading completes, but validation/recovery may still be running).
  // Hide it once the manager exposes the usable DB. useLayoutEffect runs synchronously
  // before the browser paints, so there is no flash where App.tsx hides the
  // boot screen and this effect re-shows it on the next frame.
  useLayoutEffect(() => {
    const bootScreen = document.getElementById('boot-screen');
    if (!bootScreen) return;
    if (isOpening) {
      bootScreen.classList.remove('hidden');
    } else {
      bootScreen.classList.add('hidden');
    }
  }, [isOpening]);

  if (status === 'error') {
    return (
      <LocalDatabaseErrorScreen
        title="Local Database Error"
        description="The local database could not be initialized. Retry without the cached update flag, or reset this device's local data if the error persists."
        error={dbError}
        dbNames={['firesquirrel-system']}
      />
    );
  }

  if (isOpening) {
    return null; // HTML boot splash stays visible
  }

  return (
    <SystemDbContext.Provider value={ctxValue}>
      {children}
    </SystemDbContext.Provider>
  );
}
